import { useEffect, useId, useRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { GradientSpinner } from "../common/GradientSpinner";
import { cn } from "../../lib/utils";
import { apiClient, ApiClientError } from "../../lib/api-client";
import { env } from "../../lib/env";
import { useAuthStore } from "../../stores/auth-store";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import UserCircleLinear from "~icons/solar/user-circle-linear";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ModalState =
  | "idle"
  | "loading_ai"
  | "ready"
  | "ai_error"
  | "sending"
  | "sent_error";

export interface GreetingPreviewModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  candidateEmail?: string;
  projectId: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

// ---------------------------------------------------------------------------
// EmailChip — read-only pill showing an email address
// ---------------------------------------------------------------------------

function EmailChip({ email }: { email: string | undefined }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 rounded-full bg-[#f0f0f0] px-3 py-1.5">
      <Icon icon={UserCircleLinear} size="sm" className="shrink-0 text-grey" />
      <span className="truncate text-body-sm text-black-500">
        {email ?? "—"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GreetingPreviewModal
// ---------------------------------------------------------------------------

export function GreetingPreviewModal({
  open,
  onClose,
  candidateId,
  candidateName,
  candidateEmail,
  projectId,
  onSuccess,
  onError: _onError,
}: GreetingPreviewModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const { user } = useAuthStore.getState();

  const [state, setState] = useState<ModalState>("idle");
  const [subject, setSubject] = useState("Exciting Opportunity");
  const [emailContent, setEmailContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Stable IDs for accessibility
  const subjectId = useId();
  const contentId = useId();

  // Sync dialog open/close with `open` prop
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }

    const handleClose = () => onCloseRef.current();
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, [open]);

  // Trigger AI generation when modal opens; reset when it closes
  useEffect(() => {
    if (open) {
      void generateGreeting();
    } else {
      // Reset form state when modal closes
      setState("idle");
      setSubject("Exciting Opportunity");
      setEmailContent("");
      setError(null);
    }
    // We only want this to run when `open` changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------------------------------------------------------------------------
  // AI greeting generation
  // ---------------------------------------------------------------------------

  async function generateGreeting() {
    setState("loading_ai");
    setError(null);
    try {
      const aiApiUrl = env.AI_API_URL;
      const response = await fetch(
        `${aiApiUrl}/api/ai/v1/tools/create-email-greeting`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
          },
          body: JSON.stringify({
            candidate_id: candidateId,
            project_id: projectId,
          }),
        }
      );
      const data = (await response.json()) as {
        success?: boolean;
        data?: { email_content?: string };
      };
      if (data.success && data.data?.email_content) {
        setEmailContent(data.data.email_content);
        setSubject("Exciting Opportunity");
        setState("ready");
      } else {
        setState("ai_error");
        setError(
          "Failed to generate greeting. You can write your own message."
        );
      }
    } catch {
      setState("ai_error");
      setError("Failed to generate greeting. You can write your own message.");
    }
  }

  // ---------------------------------------------------------------------------
  // Send greeting
  // ---------------------------------------------------------------------------

  async function handleSend() {
    if (state === "loading_ai" || state === "sending") return;
    setState("sending");
    setError(null);
    try {
      await apiClient.post(`/candidates/${candidateId}/greeting`, {
        type: "email",
        project_id: projectId,
        subject,
        message: emailContent,
      });
      onSuccess(`Greeting email sent to ${candidateName}`);
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.errors.length > 0
          ? err.errors[0]
          : "Failed to send greeting email. Please try again.";
      setState("sent_error");
      setError(message);
    }
  }

  // ---------------------------------------------------------------------------
  // Backdrop click closes the modal (unless sending)
  // ---------------------------------------------------------------------------

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget && state !== "sending") {
      onClose();
    }
  };

  const isBusy = state === "loading_ai" || state === "sending";
  const canSend = !isBusy && emailContent.trim().length > 0;

  const isAiLoading = state === "loading_ai";
  const showForm =
    state === "ready" ||
    state === "ai_error" ||
    state === "sending" ||
    state === "sent_error";

  return (
    <dialog
      ref={dialogRef}
      aria-label="Contact Candidate via Email"
      onClick={handleBackdropClick}
      className="m-auto h-fit w-full max-w-[560px] rounded-2xl bg-white p-0 motion-safe:animate-modal-in backdrop:bg-black/50 backdrop:motion-safe:animate-modal-backdrop-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#f0f0f0] px-6 py-5">
        <h2 className="text-body-xl font-semibold text-black-500">
          Contact Candidate via Email
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={state === "sending"}
          aria-label="Close modal"
          className="cursor-pointer text-grey transition-colors hover:text-black-500 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Icon icon={CloseCircleLinear} size="xl" />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-6 py-6">
        {/* From / To row */}
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <span className="text-body-sm font-medium text-grey">From</span>
            <EmailChip email={user?.email} />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <span className="text-body-sm font-medium text-grey">To</span>
            <EmailChip email={candidateEmail} />
          </div>
        </div>

        {/* AI loading state */}
        {isAiLoading && (
          <div className="flex min-h-[250px] flex-col items-center justify-center gap-3 rounded-[20px] border border-black-50">
            <GradientSpinner size={32} />
            <p className="text-body-sm text-grey">Generating AI greeting...</p>
          </div>
        )}

        {/* AI error banner */}
        {state === "ai_error" && error && (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[#fff3f5] px-4 py-3">
            <p className="text-body-sm text-error">{error}</p>
            <button
              type="button"
              onClick={() => void generateGreeting()}
              className="shrink-0 cursor-pointer rounded-full border border-error px-3 py-1 text-body-sm font-medium text-error transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}

        {/* Send error banner */}
        {state === "sent_error" && error && (
          <div className="rounded-xl bg-[#fff3f5] px-4 py-3">
            <p className="text-body-sm text-error">{error}</p>
          </div>
        )}

        {/* Editable form — shown once AI content is ready (or ai_error/sent_error allow manual entry) */}
        {showForm && (
          <>
            {/* Subject */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={subjectId}
                className="text-body-sm font-medium text-grey"
              >
                Subject
              </label>
              <input
                id={subjectId}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isBusy}
                placeholder="Enter subject"
                className={cn(
                  "w-full rounded-full border border-black-50 px-4 py-3 text-body-md text-black-500 outline-none placeholder:text-grey transition-colors",
                  "focus:border-primary",
                  isBusy && "opacity-60"
                )}
              />
            </div>

            {/* Email Content */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={contentId}
                className="text-body-sm font-medium text-grey"
              >
                Email Content
              </label>
              <textarea
                id={contentId}
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                disabled={isBusy}
                placeholder="Write your message..."
                className={cn(
                  "w-full min-h-[250px] resize-y rounded-[20px] border border-black-50 px-4 py-3 text-body-sm text-black-500 outline-none placeholder:text-grey transition-colors",
                  "focus:border-primary",
                  isBusy && "opacity-60"
                )}
              />
            </div>

            {/* Helper text */}
            <p className="text-body-xs text-grey">
              This email was drafted by AI based on the candidate&apos;s
              profile. Feel free to edit before sending.
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 border-t border-[#f0f0f0] px-6 py-5">
        <button
          type="button"
          onClick={onClose}
          disabled={state === "sending"}
          className="cursor-pointer rounded-full border border-grey-100 px-6 py-3 text-body-md font-bold text-grey transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          className={cn(
            "rounded-full px-6 py-3 text-body-md font-bold text-white transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            canSend
              ? "cursor-pointer bg-gradient-to-r from-[#ff3072] to-[#750087] hover:opacity-90"
              : "cursor-not-allowed bg-gradient-to-r from-[#ff3072] to-[#750087] opacity-50"
          )}
        >
          {state === "sending" ? (
            <span className="flex items-center gap-2">
              <GradientSpinner size={16} className="[&_path]:fill-white" />
              Sending...
            </span>
          ) : (
            "Send Email"
          )}
        </button>
      </div>
    </dialog>
  );
}
